import Switch from '@mui/material/Switch';
import { useNavigate } from 'react-router-dom';
import * as React from 'react';
import Event from '../../services/interfaces/live-event-interface';
import EventService from '../../services/event-service';
import ManageLiveEventService from '../Go Live/ManageLiveEventService';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { isLoggedIn } from '../../services/isLoggedInService';
import { updateVisibility } from '../../services/visibilityPublishService';
import { RingLoader } from 'react-spinners';
import { GiUpgrade } from "react-icons/gi";
import { AxiosInstance } from '../../common/Loader/axios.config';

const ITEM_HEIGHT = 48;

interface LiveEventProps {
  loggedInProvider: { id: string; userType: string };
}

const LiveEvent: React.FC<LiveEventProps> = ({ loggedInProvider }) => {
  const [vods, setVods] = React.useState<Event[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const manageLiveEventService = new ManageLiveEventService();

  React.useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/auth/signin');
    }
  }, [navigate]);

  // Fetch and normalize events
  React.useEffect(() => {
    const fetchVods = async () => {
      try {
        setLoading(true);
        const fetchedVods = await EventService.getNewSchemaEvents();

        const normalizedVods = fetchedVods
          .map((vod) => ({
            ...vod,
            status: {
              type: vod.status?.type || 'normal',
              lifetime: vod.status?.lifetime ?? 0,
              visibility: Boolean(vod.status?.visibility),
              publish: vod.status?.publish === 'true' || vod.status?.publish === true,
              promoted: Boolean(vod.status?.promoted),
              live: Boolean(vod.status?.live),
            },
            video_list: Array.isArray(vod.video_list) ? vod.video_list : [],
            providerObj: Array.isArray(vod.providerObj) ? vod.providerObj : [],
            projectObj: Array.isArray(vod.projectObj) ? vod.projectObj : [],
            productObj: Array.isArray(vod.productObj) ? vod.productObj : [],
          }))
          .filter((vod) => vod.category === 'event');

        setVods(normalizedVods);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching events:', error);
        setLoading(false);
      }
    };

    fetchVods();
  }, []);

  // Toggle visibility or publish
  const handleToggleChange = async (
    eventId: string,
    field: 'visibility' | 'publish',
    newValue: boolean
  ) => {
    try {
      const currentVod = vods.find((vod) => vod._id === eventId);
      if (!currentVod) throw new Error('Video not found');

      const updateOptions = {
        visibility: field === 'visibility' ? newValue : currentVod.status.visibility,
        publish: field === 'publish' ? newValue : currentVod.status.publish,
      };

      await updateVisibility(eventId, 'events', updateOptions);

      setVods((prevVods) =>
        prevVods.map((vod) =>
          vod._id === eventId
            ? { ...vod, status: { ...vod.status, [field]: newValue } }
            : vod
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Promote / Demote
  const handlePromote = async (eventId: string, promote: boolean) => {
    try {
      if (promote) {
        await manageLiveEventService.promoteToTrending(eventId);
      } else {
        await manageLiveEventService.depromoteToTrending(eventId);
      }

      setVods((prevVods) =>
        prevVods.map((vod) =>
          vod._id === eventId
            ? { ...vod, status: { ...vod.status, promoted: promote } }
            : vod
        )
      );
    } catch (error) {
      console.error('Error updating promotion status:', error);
    }
  };

  // Filter VODs for logged-in provider and search
  const [filterName, setFilterName] = React.useState<string>('');
  const [sortBy, setSortBy] = React.useState<'views' | 'comments' | 'name'>('views');

  const filteredVods = vods
    .filter((vod) => loggedInProvider.userType === 'admin' || vod.video_provider === loggedInProvider.id)
    .filter((vod) => vod.title?.toLowerCase().includes(filterName.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'views') return (b.views || 0) - (a.views || 0);
      if (sortBy === 'comments') return (b.comments || 0) - (a.comments || 0);
      return (a.title || '').localeCompare(b.title || '');
    });

  return (
    <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-transparent dark:bg-transparent w-[1150px]">
      <div className="m-4 mt-2 flex justify-between px-4">
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-sm">Sort by:</span>
          <select
            className="rounded border px-2 py-1 text-sm"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'views' | 'comments' | 'name')}
          >
            <option value="views">Views</option>
            <option value="comments">Comments</option>
            <option value="name">Name</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-sm">Filter by name:</span>
          <input
            type="text"
            className="rounded-full border px-3 py-1"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-12 border-t border-stroke px-4 py-2 font-medium">
        <div className="col-span-5">Name</div>
        <div className="col-span-1 hidden sm:flex justify-center">Clicks</div>
        <div className="col-span-1 flex justify-center">Views</div>
        <div className="col-span-1 flex justify-center">Comments</div>
        <div className="col-span-1 flex justify-center">Visibility</div>
        <div className="col-span-1 flex justify-center">Publish</div>
      </div>

      <div className="m-4 mt-2 h-screen overflow-y-scroll">
        {loading ? (
          <RingLoader color="#36D7B7" loading={loading} size={100} />
        ) : (
          filteredVods.map((vod) => (
            <div
              key={vod._id}
              className="grid grid-cols-12 border-t border-stroke px-4 py-2"
            >
              <div className="col-span-5 flex items-center gap-4">
                <img
                  src={vod.thumb_nail}
                  alt={vod.title}
                  className="h-20 w-20 rounded-md"
                />
                {vod.status.promoted && <GiUpgrade className="text-success" size={32} />}
                <p>{vod.title}</p>
              </div>

              <div className="col-span-1 hidden sm:flex justify-center">{vod.video_list?.length || 0}</div>
              <div className="col-span-1 flex justify-center">{vod.views || 0}</div>
              <div className="col-span-1 flex justify-center">{vod.comments || 0}</div>

              <div className="col-span-1 flex justify-center">
                <Switch
                  color="success"
                  checked={vod.status.visibility}
                  onChange={() =>
                    handleToggleChange(vod._id, 'visibility', !vod.status.visibility)
                  }
                />
              </div>

              <div className="col-span-1 flex justify-center">
                <Switch
                  color="success"
                  checked={vod.status.publish}
                  onChange={() =>
                    handleToggleChange(vod._id, 'publish', !vod.status.publish)
                  }
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LiveEvent;
